import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FoodDetailPanel } from "./FoodDetailPanel";
import { LabelLink } from "./LabelLink";
import { hasBlockingConcern } from "./concerns";
import {
  Badge,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  foodsChanged,
} from "./shared";
import { IngestReviewItem, PassioFood } from "./types";
import {
  CatalogApi,
  ContributionsFilter,
  extractApiError,
  extractIngestResult,
} from "./useCatalogApi";
import { useLabelSuggestions } from "./useLabelSuggestions";

interface ContributionsTabProps {
  api: CatalogApi;
  ingestChainId: string;
  customerName?: string;
}

const PAGE_SIZE = 25;

const FILTERS: Array<{ id: ContributionsFilter; label: string; hint: string }> =
  [
    { id: "all", label: "All", hint: "Everything you've added or edited" },
    { id: "added", label: "Added", hint: "New products you created" },
    { id: "edited", label: "Edited", hint: "Existing products you changed" },
  ];

/** Short label for a food row. */
const rowSubtitle = (f: PassioFood): string => {
  const parts: string[] = [];
  if (f.brandName) parts.push(f.brandName);
  const upc = f.branded?.productCode;
  if (upc) parts.push(`UPC ${upc}`);
  return parts.join(" · ") || "—";
};

const ContributionsTab: React.FC<ContributionsTabProps> = ({
  api,
  ingestChainId,
  customerName,
}) => {
  const [filter, setFilter] = useState<ContributionsFilter>("all");
  const [items, setItems] = useState<PassioFood[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState<PassioFood | null>(null);
  const [draft, setDraft] = useState<PassioFood | null>(null);
  const labelSuggestions = useLabelSuggestions(api, draft);
  const [dirtySinceRecheck, setDirtySinceRecheck] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  // Blocking concern codes on the current draft (from the last validation).
  const [hasBlocking, setHasBlocking] = useState(false);

  // Only allow a save when the draft actually differs from the loaded record.
  const changed = useMemo(
    () => foodsChanged(draft, selected),
    [draft, selected]
  );

  const [reviewRunning, setReviewRunning] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("");
  const [reviewItem, setReviewItem] = useState<IngestReviewItem | null>(null);
  const [reviewError, setReviewError] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(
    async (f: ContributionsFilter, p: number) => {
      setLoading(true);
      setListError("");
      try {
        const res = await api.getMyContributions(f, p, PAGE_SIZE);
        setItems(res.items);
        setTotal(res.total);
        if (res.items.length === 0) {
          setListError(
            f === "all"
              ? "You haven't added or edited any catalog products yet."
              : `No ${f === "added" ? "added" : "edited"} products yet.`
          );
        }
      } catch (err) {
        setItems([]);
        setTotal(0);
        setListError(extractApiError(err, "Failed to load your contributions"));
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  useEffect(() => {
    void load(filter, page);
  }, [load, filter, page]);

  const changeFilter = (f: ContributionsFilter) => {
    if (f === filter) return;
    setFilter(f);
    setPage(1);
  };

  const select = (f: PassioFood) => {
    setSelectedId(f.id || "");
    setSelected(f);
    setDraft(f);
    setDirtySinceRecheck(false);
    setSaveMsg("");
    setSaveErr("");
    setHasBlocking(hasBlockingConcern(f.concerns));
    setReviewItem(null);
    setReviewError("");
  };

  const recheck = async () => {
    if (!draft) return;
    setRechecking(true);
    setSaveErr("");
    setSaveMsg("");
    try {
      const res = await api.recheckConcerns(draft);
      setDraft(res.food);
      setDirtySinceRecheck(false);
      const blocking = res.blocking ?? hasBlockingConcern(res.concern_codes);
      setHasBlocking(blocking);
      setSaveMsg(
        blocking
          ? "Re-checked — still has blocking concern codes; fix the flagged fields before saving."
          : "Re-checked — no blocking concerns. Good to save."
      );
    } catch (err) {
      setSaveErr(extractApiError(err, "Re-check failed"));
    } finally {
      setRechecking(false);
    }
  };

  const handleEditingChange = (isEditing: boolean) => {
    if (!isEditing && dirtySinceRecheck && !rechecking) void recheck();
  };

  const save = async () => {
    if (!selected || !draft || !selected.id) return;
    if (!changed) {
      setSaveMsg("");
      setSaveErr("No changes to save.");
      return;
    }
    setSaving(true);
    setSaveMsg("");
    setSaveErr("");
    try {
      // Always re-run the concern-code sanity checks against the current edits
      // before persisting, and refuse to save if anything blocks (same rule the
      // Add Products review enforces before a write).
      const check = await api.recheckConcerns(draft);
      const validated = check.food;
      setDraft(validated);
      setDirtySinceRecheck(false);
      const blocking =
        check.blocking ?? hasBlockingConcern(check.concern_codes);
      setHasBlocking(blocking);
      if (blocking) {
        setSaveErr(
          "Can't save — this product still has blocking concern codes. Fix the flagged fields (highlighted in the editor), then save."
        );
        return;
      }
      const updated: PassioFood = { ...validated, id: selected.id };
      const result = await api.updateFood(updated);
      await api.notifyChange({
        action: "update",
        food: result,
        original: selected,
        customerName,
      });
      setSelected(result);
      setDraft(result);
      setHasBlocking(false);
      setItems((list) =>
        list.map((it) => (it.id === selected.id ? result : it))
      );
      setSaveMsg("Saved and ops notified.");
    } catch (err) {
      setSaveErr(extractApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const reReview = async () => {
    if (!selected) return;
    setReviewRunning(true);
    setReviewError("");
    setReviewItem(null);
    try {
      const resp = await api.runChain(
        ingestChainId,
        { food_input: JSON.stringify(selected) },
        {
          metadata: {
            feature: "contributions-re-review",
            food_id: selected.id,
          },
          onStatus: (s) => setReviewStatus(s),
        }
      );
      const result = extractIngestResult(resp);
      setReviewItem(result.items[0] || null);
      if (!result.items[0])
        setReviewError("Re-research returned no proposed record.");
    } catch (err) {
      setReviewError(extractApiError(err, "Re-research failed"));
    } finally {
      setReviewRunning(false);
    }
  };

  const applyReReview = async () => {
    if (!selected?.id || !reviewItem) return;
    const proposed = (reviewItem.ingest_payload || reviewItem.proposed) as
      | PassioFood
      | undefined;
    if (!proposed) return;
    if (hasBlockingConcern(proposed.concerns)) {
      setSaveErr(
        "Can't apply — the re-researched record has blocking concern codes. Open it in the full review page to resolve them first."
      );
      return;
    }
    setSaving(true);
    setSaveErr("");
    setSaveMsg("");
    try {
      const updated: PassioFood = { ...proposed, id: selected.id };
      const result = await api.updateFood(updated);
      await api.notifyChange({
        action: "update",
        food: result,
        original: selected,
        customerName,
      });
      setSelected(result);
      setDraft(result);
      setHasBlocking(false);
      setReviewItem(null);
      setItems((list) =>
        list.map((it) => (it.id === selected.id ? result : it))
      );
      setSaveMsg("Updated from re-research and ops notified.");
    } catch (err) {
      setSaveErr(extractApiError(err, "Apply failed"));
    } finally {
      setSaving(false);
    }
  };

  const rangeLabel = useMemo(() => {
    if (total === 0) return "";
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(total, (page - 1) * PAGE_SIZE + items.length);
    return `${start}–${end} of ${total}`;
  }, [total, page, items.length]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* List column */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Your products &amp; contributions
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Everything you&apos;ve added or edited through the Catalog Manager, so
          you can find and fix your own items.
        </p>

        <div className="mt-3 inline-flex rounded-md border border-gray-200 bg-gray-50 p-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => changeFilter(f.id)}
              title={f.hint}
              className={`rounded px-3 py-1.5 text-sm font-semibold transition ${
                filter === f.id
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <span>{loading ? "Loading…" : rangeLabel}</span>
          <div className="flex items-center gap-2">
            <SecondaryButton
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              Prev
            </SecondaryButton>
            <span>
              {page} / {totalPages}
            </span>
            <SecondaryButton
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              Next
            </SecondaryButton>
          </div>
        </div>

        {listError && (
          <div className="mt-2 text-sm text-gray-500">{listError}</div>
        )}

        <div className="mt-3 max-h-[32rem] space-y-2 overflow-auto pr-1">
          {loading && items.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
              <Spinner label="Loading your contributions…" />
            </div>
          ) : (
            items.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => select(f)}
                className={`block w-full rounded-md border p-3 text-left transition hover:border-indigo-300 ${
                  selectedId === f.id
                    ? "border-indigo-400 bg-indigo-50"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-gray-900">
                    {f.name || "(unnamed)"}
                  </span>
                  {f.concerns && f.concerns.length > 0 && (
                    <Badge tone="amber">{f.concerns.length} concern(s)</Badge>
                  )}
                </div>
                <div className="text-xs text-gray-500">{rowSubtitle(f)}</div>
                {f.tags && f.tags.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {f.tags.slice(0, 4).map((t) => (
                      <Badge key={t} tone="blue">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Editor column */}
      <div>
        {!selected || !draft ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
            Pick one of your products to edit any field (incl. nutrients &amp;
            portions), or re-research it against the latest online data.
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit product
              </h3>
              <span className="text-xs text-gray-400">id {selected.id}</span>
            </div>

            <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3">
              <FoodDetailPanel
                food={draft}
                onChange={(next) => {
                  setDraft(next);
                  setDirtySinceRecheck(true);
                }}
                onEditingChange={handleEditingChange}
                onRecheck={recheck}
                rechecking={rechecking}
                labelSlot={
                  selected.id ? (
                    <LabelLink
                      api={api}
                      foodId={selected.id}
                      suggestions={labelSuggestions}
                    />
                  ) : undefined
                }
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <PrimaryButton
                type="button"
                onClick={save}
                disabled={saving || rechecking || !changed || hasBlocking}
                title={
                  !changed
                    ? "No changes to save"
                    : hasBlocking
                    ? "Fix the blocking concern codes before saving"
                    : undefined
                }
              >
                {saving ? "Saving…" : "Save changes"}
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={recheck}
                disabled={rechecking || saving || !draft}
                title="Re-run the concern-code sanity checks against your edits"
              >
                {rechecking ? "Validating…" : "Validate"}
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={reReview}
                disabled={reviewRunning || !ingestChainId}
                title="Re-runs the AI research chain on this product against the latest online sources and proposes a refreshed record you can apply."
              >
                {reviewRunning ? "Re-researching…" : "Re-research online"}
              </SecondaryButton>
              {reviewRunning && <Spinner label={`status: ${reviewStatus}`} />}
            </div>
            {!changed ? (
              <p className="mt-1.5 text-xs text-gray-400">
                Edit a field to enable saving.
              </p>
            ) : hasBlocking ? (
              <p className="mt-1.5 text-xs font-medium text-red-600">
                ⚠ Blocking concern codes must be cleared before this can be
                saved. Fix the highlighted fields, then Validate.
              </p>
            ) : null}

            {saveMsg && (
              <div className="mt-2 text-sm text-green-700">{saveMsg}</div>
            )}
            {saveErr && (
              <div className="mt-2 text-sm text-red-600">{saveErr}</div>
            )}
            {reviewError && (
              <div className="mt-2 text-sm text-red-600">{reviewError}</div>
            )}

            {reviewItem && (
              <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50 p-3">
                <div className="text-sm font-semibold text-indigo-900">
                  Re-research proposal
                </div>
                <p className="text-xs text-indigo-700">
                  Latest online data, formatted for the database. Review and
                  apply if it&apos;s an improvement (updates this item in
                  place).
                </p>
                <div className="mt-2 rounded-md border border-gray-100 bg-white p-3">
                  <FoodDetailPanel
                    food={
                      (reviewItem.ingest_payload ||
                        reviewItem.proposed) as PassioFood
                    }
                  />
                </div>
                <PrimaryButton
                  type="button"
                  className="mt-2"
                  onClick={applyReReview}
                  disabled={saving}
                >
                  Apply update from re-research
                </PrimaryButton>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContributionsTab;
